import VectorSource from "ol/source/Vector";
import {RenderCounts} from "./Renderer";
import {Hill} from "./Hill";
import {Visit} from "./Bag";
import {Fill, Stroke, Style} from "ol/style";
import CircleStyle from "ol/style/Circle";
import {Feature} from "ol";
import {LineString, Point} from "ol/geom";
import {fromLonLat} from "ol/proj";
import {GPXBuilder} from "./GPXBuilder";
import LayerGroup from "ol/layer/Group";

export abstract class LayerDefBase {
    protected abstract getVisitedStyle(): Style;
    protected abstract getUnvisitedStyle(): Style;
    protected abstract getLayerHills(hills: HillLists): Hill[];

    private visitedSource?: VectorSource;
    private unvisitedSource?: VectorSource;
    private group?: LayerGroup;
    private counts?: RenderCounts;
    private layerHills: Hill[] = [];
    private visits: Visit[] = [];

    protected isInitiallyVisible: boolean = false;

    getInitiallyVisible(): boolean {
        return this.isInitiallyVisible;
    }

    public setInitiallyVisible(): void {
        this.isInitiallyVisible = true;
    }

    public setVisitedSource(visitedSource: VectorSource) {
        this.visitedSource = visitedSource;
    }

    getVisitedSource(): VectorSource {
        return this.visitedSource as VectorSource;
    }

    public setUnvisitedSource(unvisitedSource: VectorSource) {
        this.unvisitedSource = unvisitedSource;
    }

    public getUnvisitedSource(): VectorSource {
        return this.unvisitedSource as VectorSource;
    }

    public setGroup(group: LayerGroup) {
        this.group = group;
    }

    public getGroup(): LayerGroup {
        return this.group as LayerGroup;
    }

    getCounts(): RenderCounts {
        return this.counts as RenderCounts;
    }

    getHills(): Hill[] {
        return this.layerHills;
    }

    setupGPX(): void {
        //Do nothing by default
    }

    setProgressBar(): void {
        const progElem = this.getProgressElement();
        if (!progElem) {
            return;
        }

        const counts = this.getCounts();
        const w = ((counts.done * 100) / counts.total).toFixed(1);
        progElem.style.width = `${w}%`;

        const countElem = this.getCountElement();
        if (!countElem) {
            return;
        }
        countElem.innerText = `${counts.done} / ${counts.total}`;
    }

    getProgressElement(): HTMLDivElement | undefined {
        return undefined;
    }

    getCountElement(): HTMLDivElement | undefined {
        return undefined;
    }

    render(hills: HillLists, visits: Visit[]) {
        this.layerHills = this.getLayerHills(hills);
        this.visits = visits;
        this.drawLines(hills.main, this.layerHills);
        this.counts = this.doRender(this.layerHills, visits);
    }

    private drawLines(hills: Hill[], layerHills: Hill[]): void {
        const hillMap = new Map<number, Hill>();
        hills.forEach((h) => {
            hillMap.set(h.id, h);
        });
        layerHills
            .filter((h) => h.parent && h.parent !== h.id)
            .forEach((h) => {
                if (!h.parent) {
                    return;
                }
                const parent = hillMap.get(h.parent);
                if (!parent) {
                    return;
                }
                const startPoint = new Point(fromLonLat([h.lon, h.lat]));
                const endPoint = new Point(
                    fromLonLat([parent.lon, parent.lat])
                );

                const feature = new Feature({
                    geometry: new LineString([
                        startPoint.getCoordinates(),
                        endPoint.getCoordinates(),
                    ]),
                });
                feature.setStyle(LayerDefBase.LINE_STYLE);
                this.getVisitedSource().addFeature(feature);
            });
    }

    private doRender(hills: Hill[], visits: Visit[]): RenderCounts {
        let total = 0;
        let done = 0;
        const visitMap = new Map<Number, Visit[]>();

        const visited = this.getVisitedSource();
        const unvisited = this.getUnvisitedSource();

        hills
            .map((h) => {
                const hillVisits = visits.filter((v) => v.ids.includes(h.id));
                visitMap.set(h.id, hillVisits);
                return h;
            })
            .sort((a, b) => {
                const aVisit = visitMap.get(a.id)?.length ? 1 : 0;
                const bVisit = visitMap.get(b.id)?.length ? 1 : 0;
                if (aVisit != bVisit) {
                    return aVisit - bVisit;
                }
                return a.elev - b.elev;
            })
            .forEach((h) => {
                total++;
                const hillVisits = visitMap.get(h.id) || [];

                let iconFeature = new Feature({
                    geometry: new Point(fromLonLat([h.lon, h.lat])),
                    hill: h,
                    visits: hillVisits,
                });

                let isBagged = Boolean(hillVisits.length);
                done += isBagged ? 1 : 0;
                const markerStyle = isBagged
                    ? this.getVisitedStyle()
                    : this.getUnvisitedStyle();
                iconFeature.setStyle(markerStyle);

                let source = isBagged ? visited : unvisited;
                source.addFeature(iconFeature);
            });
        return {done, total};
    }

    protected setGPXClickListener(spanElem: HTMLSpanElement, fileName: string) {
        spanElem.addEventListener("click", () => {
            const gpxBuilder = new GPXBuilder(this.layerHills, this.visits);
            const gpxString = gpxBuilder.build();
            const data = new Blob([gpxString], {type: "text/plain"});
            const url = window.URL.createObjectURL(data);
            const a = document.createElement("a");
            a.style.display = "none";
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
        });
    }

    protected removeWainwrights(hills: Hill[]): Hill[] {
        return hills
            .filter((h) => !h.categories.includes("WAINWRIGHT"))
            .filter((h) => !h.categories.includes("WAINWRIGHT_OUTLIER"));
    }

    static buildStyle(
        radius: number,
        fill: string,
        stroke = "white",
        width = 2
    ) {
        return new Style({
            image: new CircleStyle({
                radius,
                fill: new Fill({color: fill}),
                stroke: new Stroke({color: stroke, width}),
            }),
        });
    }

    static LINE_STYLE = new Style({
        stroke: new Stroke({
            color: "#888",
            width: 1,
        }),
    });
}

export type HillLists = {
    main: Hill[];
    others: Hill[];
    lakes: Hill[];
};
